import { useFeatureGate } from "@statsig/react-bindings";
import { sumBy } from "lodash";
import { useCallback, useMemo } from "react";

import { components } from "@/@types/api";
import { Box } from "@/Components/v2/atoms/Box";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useConfig } from "@/hooks/useConfig";

import { ScenarioOption } from "../../components/NewActionPlanModal/types";
import { useGeoEnabled } from "../../hooks/useGeoEnabled";
import { formatPercentageFromFraction } from "../../utils/formatPercentage";
import { calculateROI } from "../../utils/spend-revenue-summary";
import {
  useReportSectionTracking,
  useScenarioAnalyticsV3,
} from "../../utils/useScenarioAnalyticsV3";
import { StatusEnum } from "../scenario-form/types";
import { HighLevelOverview } from "./components/HighLevelOverview";
import { RevenueBreakdown } from "./components/RevenueBreakdown";
import { ROIBreakdown } from "./components/ROIBreakdown";
import { ScenarioBreakdownTable } from "./components/ScenarioBreakdownTable";
import { ScenarioErrorState } from "./components/ScenarioErrorState";
import { ScenarioProcessingState } from "./components/ScenarioProcessingState";
import { useScenarioResultsContext } from "./ScenarioResultsProvider";
import ScenarioActionBanner from "./components/ScenarioActionBanner";

type ScenarioResultRevenue = components["schemas"]["ScenarioResultRevenue"];
type ScenarioResultSpend = components["schemas"]["ScenarioResultSpend"];

type ScenarioResultPrimaryMetrics = {
  simulatedSpend: number;
  simulatedRevenue: number;
  minimumRevenue: number;
  maximumRevenue: number;
  minimumROI: number | undefined;
  maximumROI: number | undefined;
  averageROI: number | undefined;
};

const emptyScenarioResultPrimaryMetrics: ScenarioResultPrimaryMetrics = {
  simulatedSpend: 0,
  simulatedRevenue: 0,
  minimumRevenue: 0,
  maximumRevenue: 0,
  minimumROI: undefined,
  maximumROI: undefined,
  averageROI: undefined,
};

export const getResultPrimaryMetrics = (
  revenues: ScenarioResultRevenue[],
  spends: ScenarioResultSpend[],
  confidence: number
): ScenarioResultPrimaryMetrics => {
  const simulatedSpend = sumBy(spends, "spend");
  if (simulatedSpend === 0) {
    return emptyScenarioResultPrimaryMetrics;
  }

  const simulatedRevenue = sumBy(revenues, "revenue");

  const minimumRevenue = simulatedRevenue - confidence;
  const maximumRevenue = simulatedRevenue + confidence;

  const ROI = calculateROI({ spend: simulatedSpend, revenue: simulatedRevenue });
  const minimumROI = calculateROI({ spend: simulatedSpend, revenue: minimumRevenue });
  const maximumROI = calculateROI({ spend: simulatedSpend, revenue: maximumRevenue });

  return {
    simulatedSpend,
    simulatedRevenue,
    minimumRevenue,
    maximumRevenue,
    minimumROI,
    maximumROI,
    averageROI: ROI,
  };
};

export const ScenariosResultsPage = () => {
  const { config } = useConfig();
  const geoEnabledQuery = useGeoEnabled();
  const isGeoEnabled = geoEnabledQuery.isLoading ? false : geoEnabledQuery.isEnabled;
  const { mapDefaultNodeName } = useDefaultNodeNameInfo();

  const { value: isActionPlansEnabled } = useFeatureGate(featureFlag.scenarioBuilderActionPlans);

  const { scenario, revenues, spends, selectedGeography } = useScenarioResultsContext();

  // Initialize v3 analytics
  const { trackReportPageDuration } = useScenarioAnalyticsV3({ scenario });

  // Track report section viewing duration
  useReportSectionTracking(trackReportPageDuration, "Scenario Results Page", {
    trackingInterval: 30_000, // 30 seconds
    minimumDuration: 3_000, // 3 seconds
  });

  // Define completed statuses
  const completedStatuses = [StatusEnum.Approved, StatusEnum.Archived, StatusEnum.Completed];
  const isScenarioCompleted = completedStatuses.includes(scenario?.scenarioStatus as StatusEnum);

  const {
    simulatedSpend,
    simulatedRevenue,
    minimumRevenue,
    maximumRevenue,
    minimumROI,
    maximumROI,
    averageROI,
  } = useMemo(() => {
    if (!scenario?.scenarioReport) {
      return emptyScenarioResultPrimaryMetrics;
    }

    return getResultPrimaryMetrics(revenues, spends, scenario.scenarioReport.confidence ?? 0);
  }, [revenues, spends, scenario?.scenarioReport]);

  const summaryFormatter = useCallback(
    (value: number) =>
      config.currency.formatCurrency(value, {
        abbreviate: true,
        minAbbreviatedRange: 1e6,
      }),
    [config.currency]
  );

  // Show processing state if scenario is still processing
  if (scenario?.scenarioStatus === StatusEnum.Processing) {
    return <ScenarioProcessingState />;
  }

  // Show processing state if scenario is still processing
  if (scenario?.scenarioStatus === StatusEnum.Error) {
    return (
      <ScenarioErrorState
        scenarioId={scenario.uuid!}
        error={scenario.statusMessage ?? "Unknown error"}
      />
    );
  }

  // Only show results if scenario is completed and has report data
  if (!isScenarioCompleted || !scenario?.scenarioReport) {
    return null;
  }

  const scenarioOption: ScenarioOption = useMemo(() => {
    return {
      uuid: scenario.uuid!,
      name: scenario?.name,
      description: scenario?.description,
      businessUnit: mapDefaultNodeName(scenario?.brand ?? "default"),
      geography: scenario?.geography,
      startDate: scenario?.startDate,
      endDate: scenario?.endDate,
      plannedSpend: simulatedSpend,
      forecastRevenue: simulatedRevenue,
      estimatedROI: averageROI,
    };
  }, [scenario, mapDefaultNodeName, simulatedSpend, simulatedRevenue, averageROI]);

  const eligibleActionPlans = scenario?.eligibleActionPlans ?? [];
  const approvedActionPlans = scenario?.approvedActionPlans ?? [];

  const isShowTakeActionBanner =
    isActionPlansEnabled &&
    (scenario.scenarioStatus === StatusEnum.Approved ||
      scenario.scenarioStatus === StatusEnum.Completed);

  return (
    <Box>
      {isShowTakeActionBanner && scenario.isEligibleForActionPlans && (
        <ScenarioActionBanner
          eligibleActionPlans={eligibleActionPlans}
          approvedActionPlans={approvedActionPlans}
          scenarioOption={scenarioOption}
        />
      )}
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <HighLevelOverview
          simulatedSpend={simulatedSpend}
          simulatedRevenue={simulatedRevenue}
          minimumRevenue={minimumRevenue}
          maximumRevenue={maximumRevenue}
          minimumROI={minimumROI}
          maximumROI={maximumROI}
          averageROI={averageROI}
          selectedGeography={selectedGeography}
          summaryFormatter={summaryFormatter}
          formatPercentageFromFraction={formatPercentageFromFraction}
          revenues={revenues}
          spends={spends}
        />
      </Box>
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <RevenueBreakdown revenues={revenues} spends={spends} mb={3} />
      </Box>
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <ROIBreakdown revenues={revenues} spends={spends} mb={3} />
      </Box>
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <ScenarioBreakdownTable
          revenues={revenues}
          spends={spends}
          isGeoEnabled={isGeoEnabled}
          scenario={scenario}
          selectedGeography={selectedGeography}
        />
      </Box>
    </Box>
  );
};
